import React from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import Can from '../../../components/Can';
import type { PickingItem, PickingListoResult } from '../../../services/pickingVentaService';
import { formatCurrency } from '../../../utils/formatters';
import { formatCantidadPicking, formatComprobantePicking } from '../pickingHelpers';

function ItemsPendientesList({ items }: { items: PickingItem[] }) {
  return (
    <List dense sx={{ maxHeight: 240, overflow: 'auto', bgcolor: '#fafafa', borderRadius: 1 }}>
      {items.map((item) => (
        <ListItem key={item.nroItem}>
          <ListItemText
            primary={`${item.codigoArticulo ?? ''} — ${item.descripcion ?? ''}`}
            secondary={`Pendiente: ${formatCantidadPicking(item.cantidadPendiente)} de ${formatCantidadPicking(item.cantidadPedida)}`}
          />
        </ListItem>
      ))}
    </List>
  );
}

interface PickingParcialDialogProps {
  open: boolean;
  pendientes: PickingItem[];
  loading: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export const PickingParcialDialog: React.FC<PickingParcialDialogProps> = ({
  open,
  pendientes,
  loading,
  onClose,
  onConfirm,
}) => (
  <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
    <DialogTitle>Picking incompleto</DialogTitle>
    <DialogContent>
      <Alert severity="warning" sx={{ mb: 2 }}>
        Quedan artículos sin pickear. Si confirmás, el pedido pasa a Preparado con lo pickeado y lo
        pendiente queda en un pedido en espera.
      </Alert>
      <ItemsPendientesList items={pendientes} />
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={loading}>Seguir pickeando</Button>
      <Button
        variant="contained"
        color="warning"
        onClick={onConfirm}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
      >
        Confirmar parcial
      </Button>
    </DialogActions>
  </Dialog>
);

interface PickingListoResultadoDialogProps {
  resultado: PickingListoResult | null;
  comprobante: string;
  onClose: () => void;
  onFacturar: (pedidoId: number) => void;
}

export const PickingListoResultadoDialog: React.FC<PickingListoResultadoDialogProps> = ({
  resultado,
  comprobante,
  onClose,
  onFacturar,
}) => {
  const espera = resultado?.pedidoEnEspera;
  return (
    <Dialog open={Boolean(resultado)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Picking finalizado</DialogTitle>
      <DialogContent>
        <Alert severity="success" sx={{ mb: espera ? 2 : 0 }}>
          El pedido {comprobante} quedó {resultado?.parcial ? 'preparado en forma parcial' : 'preparado'} y
          ya está disponible para facturar.
        </Alert>
        {espera && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Se generó el pedido en espera {formatComprobantePicking(espera)}
              {espera.importeTotal != null ? ` por ${formatCurrency(espera.importeTotal)}` : ''} con lo pendiente:
            </Typography>
            <ItemsPendientesList items={espera.items ?? []} />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Seguir con otro pedido</Button>
        {resultado && (
          <Can permiso="ventas.facturas.nuevo">
            <Button variant="contained" color="success" startIcon={<ReceiptIcon />} onClick={() => onFacturar(resultado.nroFormulario)}>
              Facturar ahora
            </Button>
          </Can>
        )}
      </DialogActions>
    </Dialog>
  );
};
